import { useEntitlements } from '@/lib'
import { Button, logger, RichLiveField, type RichLiveFieldRef } from '@/ui'
import { ReconnectCalendarBanner } from '@/ui/core/reconnect-calendar-banner'
import { useWhatsAppEnabled } from '@/ui/utilities/hooks/use-whatsapp-enabled'
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import {
	candidateEventIndexQueryKey,
	type DeliveryChannel,
	type InterviewBookingTokenResponseMeetingLocationType,
	interviewBookingTokenResponseMeetingLocationType,
	stripeFeature,
	useCalendarConnectionIndex,
	useCampaignInterviewSettingsShow,
	useInterviewBookingMessageStore,
	useInterviewBookingTokenStore,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
	AlertTriangleIcon,
	CheckIcon,
	ChevronDownIcon,
	CopyIcon,
	EyeIcon,
	MailIcon,
	MessageCircleIcon,
	PencilIcon,
	XIcon,
} from 'lucide-react'
import { type ReactNode, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	type DeliverySummary,
	failedChannels,
	failedTokenIds,
	mergeChannelCounts,
	summarizeDeliveryOutcomes,
} from './delivery-outcomes'

export interface SendBookingLinkDialogProps {
	isOpen: boolean
	onClose: () => void
	campaignId: string
	candidateIds: string[]
	isBulk: boolean
	/** The candidate's name, used in the single-candidate copy. Omitted for bulk sends. */
	candidateName?: string
	onSuccess?: () => void
}

type MessageChannel = 'email' | 'whatsapp' | 'both'

const LOCATION_TYPES: InterviewBookingTokenResponseMeetingLocationType[] = [
	interviewBookingTokenResponseMeetingLocationType.jobilla_meet,
	interviewBookingTokenResponseMeetingLocationType.google_meet,
	interviewBookingTokenResponseMeetingLocationType.teams,
	interviewBookingTokenResponseMeetingLocationType.zoom,
	interviewBookingTokenResponseMeetingLocationType.phone,
	interviewBookingTokenResponseMeetingLocationType.in_person,
	interviewBookingTokenResponseMeetingLocationType.custom,
]

// Location types that need a free-text detail (phone number, address, custom instructions).
const LOCATION_TYPES_WITH_DETAILS: InterviewBookingTokenResponseMeetingLocationType[] = [
	interviewBookingTokenResponseMeetingLocationType.phone,
	interviewBookingTokenResponseMeetingLocationType.in_person,
	interviewBookingTokenResponseMeetingLocationType.custom,
]

// A Meet link only exists as a byproduct of the event on a connected Google calendar,
// Teams on Microsoft 365 — so each is offered only while calendar integration is live,
// and a concrete send is blocked unless the sender has the matching connection.
const LOCATION_REQUIRED_PROVIDER: Partial<
	Record<InterviewBookingTokenResponseMeetingLocationType, 'google' | 'microsoft_365'>
> = {
	[interviewBookingTokenResponseMeetingLocationType.google_meet]: 'google',
	[interviewBookingTokenResponseMeetingLocationType.teams]: 'microsoft_365',
}

const DEFAULT_EMAIL_BODY =
	"<p>Hi {{candidate_first_name}},</p><p>We'd like to invite you to interview for the {{campaign_title}} position. Please book a time that works for you using the button below:</p><p>{{booking_url#Button(Book your interview)}}</p><p>Looking forward to speaking with you!</p><p>{{sender_name}}</p>"

const EMAIL_PLACEHOLDERS = [
	'{{candidate_first_name}}',
	'{{candidate_name}}',
	'{{campaign_title}}',
	'{{booking_url}}',
	'{{booking_url#Button(Book now)}}',
	'{{sender_name}}',
	'{{organisation_name}}',
]

/** Reads the active form's 409-conflict candidate list off an unknown thrown error, or null otherwise. */
function getConflictCandidateIds(error: unknown): string[] | null {
	const response = (error as {
		response?: { status?: number; data?: { candidates_with_active_bookings?: string[] } }
	}).response

	if (response?.status === 409) {
		return response.data?.candidates_with_active_bookings ?? []
	}

	return null
}

function channelsToArray(channel: MessageChannel): DeliveryChannel[] {
	if (channel === 'both') {
		return ['email', 'whatsapp']
	}

	return [channel]
}

/**
 * Read-only render of the email body for the preview. Merge fields ({{...}}) are shown as
 * pills with a human-readable label — never substituted with the real value, so the recruiter
 * sees exactly which fields get filled in per candidate.
 */
function EmailBodyPreview({ html }: { html: string }): ReactNode {
	const paragraphs = html
		.split('</p>')
		.map((paragraph) => paragraph.replace(/<[^>]+>/g, '').trim())
		.filter(Boolean)

	return (
		<div className="space-y-2 px-4 py-3 text-sm leading-relaxed text-grey-800">
			{paragraphs.map((paragraph, paragraphIndex) => (
				<p key={paragraphIndex}>
					{paragraph
						.split(/(\{\{[^}]+\}\})/g)
						.filter(Boolean)
						.map((part, partIndex) => {
							const button = /^\{\{booking_url#Button\(([^)]*)\)\}\}$/.exec(part)
							if (button) {
								return (
									<span
										key={partIndex}
										className="mx-0.5 inline-block rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white"
									>
										{button[1].trim()}
									</span>
								)
							}
							const token = /^\{\{([a-z_]+)\}\}$/.exec(part)
							return token
								? (
									<span
										key={partIndex}
										className="mx-0.5 inline-block whitespace-nowrap rounded-full bg-brand-50 px-2.5 py-1 align-middle text-xs font-medium leading-none text-brand-700 ring-1 ring-inset ring-brand-600/20"
									>
										{token[1].replace(/_/g, ' ')}
									</span>
								)
								: <span key={partIndex}>{part}</span>
						})}
				</p>
			))}
		</div>
	)
}

export function SendBookingLinkDialog({
	isOpen,
	onClose,
	campaignId,
	candidateIds,
	isBulk,
	candidateName,
	onSuccess,
}: SendBookingLinkDialogProps) {
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const whatsAppEnabled = useWhatsAppEnabled()

	const { can, isLoading: entitlementsLoading } = useEntitlements()
	const jobillaMeetEnabled = !entitlementsLoading && can(stripeFeature['jobilla-meet'])

	const { data: settings } = useCampaignInterviewSettingsShow(campaignId)

	const { data: calendarConnections } = useCalendarConnectionIndex()
	// A connection flagged needs_reconnect has a dead token the event-create job
	// won't use, so it can't produce a meeting link — treat it as no connection.
	const connectedProvider = calendarConnections?.find((connection) => !connection.needsReconnect)?.provider ?? null

	const [overrideLocation, setOverrideLocation] = useState(false)
	const [locationType, setLocationType] = useState<InterviewBookingTokenResponseMeetingLocationType | null>(null)
	const [locationDetails, setLocationDetails] = useState('')

	const [sendMessage, setSendMessage] = useState(true)
	const [channel, setChannel] = useState<MessageChannel>('email')
	const [emailBody, setEmailBody] = useState(DEFAULT_EMAIL_BODY)
	const [emailView, setEmailView] = useState<'collapsed' | 'preview' | 'edit'>('collapsed')
	const [whatsappView, setWhatsappView] = useState<'collapsed' | 'preview'>('collapsed')
	const editorRef = useRef<RichLiveFieldRef>(null)

	const [conflictIds, setConflictIds] = useState<string[] | null>(null)
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const [createdBookingUrl, setCreatedBookingUrl] = useState<string | null>(null)
	const [sent, setSent] = useState(false)
	const [copied, setCopied] = useState(false)
	const [deliverySummary, setDeliverySummary] = useState<DeliverySummary | null>(null)

	const createTokens = useInterviewBookingTokenStore()
	const sendMessages = useInterviewBookingMessageStore()
	const isSending = createTokens.isPending || sendMessages.isPending

	// Override defaults to the campaign's configured location, falling back to Google Meet.
	const campaignDefaultLocation: InterviewBookingTokenResponseMeetingLocationType =
		(settings?.interviewMeetingLocationType as InterviewBookingTokenResponseMeetingLocationType | null)
			?? interviewBookingTokenResponseMeetingLocationType.google_meet
	const effectiveLocationType: InterviewBookingTokenResponseMeetingLocationType = locationType
		?? campaignDefaultLocation

	// Zoom rides an unbuilt VideoIntegrations path; Meet/Teams need calendar integration live.
	const availableLocationTypes = useMemo(
		() =>
			LOCATION_TYPES.filter((type) => {
				if (type === interviewBookingTokenResponseMeetingLocationType.zoom) {
					return false
				}
				if (type === interviewBookingTokenResponseMeetingLocationType.jobilla_meet) {
					return jobillaMeetEnabled
				}
				if (type in LOCATION_REQUIRED_PROVIDER) {
					return true
				}
				return true
			}),
		[jobillaMeetEnabled],
	)

	// Guards the effective location (campaign default OR override), so it fires even
	// while the override section is collapsed and the default silently drives the send.
	const requiredProvider = LOCATION_REQUIRED_PROVIDER[effectiveLocationType] ?? null
	const missingCalendarConnection = requiredProvider !== null && connectedProvider !== requiredProvider

	const candidateCount = candidateIds.length
	const channels = useMemo(() => channelsToArray(channel), [channel])

	const emailOn = channel === 'email' || channel === 'both'
	const whatsappOn = channel === 'whatsapp' || channel === 'both'

	if (!isOpen) {
		return null
	}

	function resetAndClose() {
		setOverrideLocation(false)
		setLocationDetails('')
		setSendMessage(true)
		setChannel('email')
		setEmailBody(DEFAULT_EMAIL_BODY)
		setEmailView('collapsed')
		setWhatsappView('collapsed')
		setConflictIds(null)
		setErrorMessage(null)
		setCreatedBookingUrl(null)
		setSent(false)
		setCopied(false)
		setDeliverySummary(null)
		onClose()
	}

	function toggleChannel(target: 'email' | 'whatsapp') {
		const selected = new Set<'email' | 'whatsapp'>()
		if (emailOn) {
			selected.add('email')
		}
		if (whatsappOn) {
			selected.add('whatsapp')
		}
		if (selected.has(target)) {
			if (selected.size > 1) {
				selected.delete(target)
			}
		} else {
			selected.add(target)
		}
		setChannel(
			selected.has('email') && selected.has('whatsapp')
				? 'both'
				: selected.has('whatsapp')
				? 'whatsapp'
				: 'email',
		)
	}

	function insertPlaceholder(placeholder: string) {
		editorRef.current?.insertText(placeholder)
	}

	async function copyUrl() {
		if (!createdBookingUrl) {
			return
		}

		// writeText rejects in insecure contexts / when permission is denied; handle it so the
		// fire-and-forget onClick doesn't surface as an unhandled rejection.
		try {
			await navigator.clipboard.writeText(createdBookingUrl)
			setCopied(true)
		} catch (error) {
			logger.error(error, { tags: { component: 'SendBookingLinkDialog', action: 'copyUrl' } })
		}
	}

	async function send(acknowledge: boolean) {
		setErrorMessage(null)

		const meetingOverride = overrideLocation
			? {
				meeting_location_type: effectiveLocationType,
				meeting_location_details: LOCATION_TYPES_WITH_DETAILS.includes(effectiveLocationType)
					? locationDetails.trim() || null
					: null,
			}
			: {}

		try {
			const result = await createTokens.mutateAsync({
				data: {
					campaignId,
					candidateIds,
					...meetingOverride,
					...(acknowledge ? { acknowledged_candidate_ids: conflictIds ?? [] } : {}),
				},
			})

			setConflictIds(null)

			if (sendMessage && result.tokens.length > 0) {
				const outcomes = await sendMessages.mutateAsync({
					data: {
						tokenIds: result.tokens.map((token) => token.id),
						deliveryChannels: channels,
						emailBody: channel === 'whatsapp' ? null : emailBody,
					},
				})

				setDeliverySummary(summarizeDeliveryOutcomes(outcomes))
			}

			for (const candidateId of candidateIds) {
				void queryClient.invalidateQueries({
					queryKey: candidateEventIndexQueryKey(candidateId),
				})
			}

			setCreatedBookingUrl(result.tokens[0]?.bookingUrl ?? null)
			setSent(true)
			onSuccess?.()
		} catch (error) {
			const conflicts = getConflictCandidateIds(error)

			if (conflicts) {
				setConflictIds(conflicts)
				return
			}

			logger.error(error, { tags: { component: 'SendBookingLinkDialog', action: 'send' } })
			setErrorMessage(error instanceof Error ? error.message : t('sendBookingLinkDialog.error.default'))
		}
	}

	async function retryFailedChannels() {
		if (!deliverySummary) {
			return
		}

		setErrorMessage(null)

		const retryChannels = failedChannels(deliverySummary)
		const retryTokenIds = failedTokenIds(deliverySummary)

		try {
			const outcomes = await sendMessages.mutateAsync({
				data: {
					tokenIds: retryTokenIds,
					deliveryChannels: retryChannels,
					emailBody: retryChannels.includes('email') ? emailBody : null,
				},
			})

			for (const candidateId of candidateIds) {
				void queryClient.invalidateQueries({ queryKey: candidateEventIndexQueryKey(candidateId) })
			}

			// Retry only touched the failed channels: carry the original sends forward
			// (so a fixed failure adds to the running delivered total), preserve the
			// non-retryable skips, and replace the failures with the retry result.
			const retrySummary = summarizeDeliveryOutcomes(outcomes)
			setDeliverySummary({
				allDelivered: retrySummary.failures.length === 0 && deliverySummary.skipCounts.length === 0,
				sentCounts: mergeChannelCounts(deliverySummary.sentCounts, retrySummary.sentCounts),
				failures: retrySummary.failures,
				failureCounts: retrySummary.failureCounts,
				skipCounts: deliverySummary.skipCounts,
			})
		} catch (error) {
			logger.error(error, { tags: { component: 'SendBookingLinkDialog', action: 'retry' } })
			setErrorMessage(error instanceof Error ? error.message : t('sendBookingLinkDialog.error.default'))
		}
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center">
			<div className="absolute inset-0 bg-black/50" onClick={resetAndClose} />

			<div className="relative z-10 flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl">
				<div className="flex items-center justify-between border-b border-grey-100 px-5 py-4">
					<h2 className="text-base font-medium text-grey-900">
						{isBulk
							? t('sendBookingLinkDialog.titleBulk', { count: candidateCount })
							: t('sendBookingLinkDialog.title', { name: candidateName })}
					</h2>
					<button type="button" onClick={resetAndClose} className="text-grey-400 hover:text-grey-700">
						<XIcon className="size-5" />
					</button>
				</div>

				{sent
					? (
						deliverySummary && !deliverySummary.allDelivered
							? (
								<div className="flex flex-col gap-4 px-5 py-4">
									<p className="text-sm font-medium text-grey-900">
										{t('sendBookingLinkDialog.partial.heading')}
									</p>
									<ul className="space-y-1.5 text-sm text-grey-700">
										{deliverySummary.sentCounts.map((sent) => (
											<li
												key={`sent-${sent.channel}`}
												className="flex items-center gap-2"
											>
												<CheckIcon className="size-3.5 shrink-0 text-green-600" />
												{t('sendBookingLinkDialog.partial.sentCount', {
													count: sent.count,
													channel: t(`sendBookingLinkDialog.channelName.${sent.channel}`),
												})}
											</li>
										))}
										{deliverySummary.failureCounts.map((failure) => (
											<li
												key={`failure-${failure.channel}`}
												className="flex items-center gap-2"
											>
												<XIcon className="size-3.5 shrink-0 text-red-500" />
												{t('sendBookingLinkDialog.partial.failedCount', {
													count: failure.count,
													channel: t(`sendBookingLinkDialog.channelName.${failure.channel}`),
												})}
											</li>
										))}
										{deliverySummary.skipCounts.map((skip) => (
											<li
												key={`skip-${skip.channel}-${skip.reason ?? 'unknown'}`}
												className="flex items-center gap-2"
											>
												<AlertTriangleIcon className="size-3.5 shrink-0 text-amber-500" />
												{skip.reason
													? t('sendBookingLinkDialog.partial.skippedCount', {
														count: skip.count,
														channel: t(`sendBookingLinkDialog.channelName.${skip.channel}`),
														reason: t(`sendBookingLinkDialog.skipReason.${skip.reason}`),
													})
													: t('sendBookingLinkDialog.partial.skippedCountUnknown', {
														count: skip.count,
														channel: t(`sendBookingLinkDialog.channelName.${skip.channel}`),
													})}
											</li>
										))}
									</ul>
									<div className="flex gap-2">
										{deliverySummary.failures.length > 0
											? (
												<Button
													type="committal"
													size="short"
													onClick={() => void retryFailedChannels()}
													isLoading={sendMessages.isPending}
												>
													{t('sendBookingLinkDialog.partial.retry')}
												</Button>
											)
											: null}
										<Button type="subtle" size="short" onClick={resetAndClose}>
											{t('sendBookingLinkDialog.done')}
										</Button>
									</div>
									{errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
								</div>
							)
							: (
								<div className="flex flex-col items-center gap-4 px-5 py-10 text-center">
									<CheckIcon className="size-10 rounded-full bg-green-100 p-2 text-green-600" />
									<p className="text-sm text-grey-700">
										{isBulk
											? t('sendBookingLinkDialog.success.bulk', { count: candidateCount })
											: t('sendBookingLinkDialog.success.individual')}
									</p>
									{!isBulk && createdBookingUrl
										? (
											<div className="flex w-full items-center gap-2 rounded-lg border border-grey-200 px-3 py-2">
												<span className="flex-1 truncate text-left text-xs text-grey-600">
													{createdBookingUrl}
												</span>
												<button
													type="button"
													onClick={copyUrl}
													className="flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
												>
													{copied
														? <CheckIcon className="size-3.5" />
														: <CopyIcon className="size-3.5" />}
													{copied
														? t('sendBookingLinkDialog.copied')
														: t('sendBookingLinkDialog.copyUrl')}
												</button>
											</div>
										)
										: null}
									<Button type="committal" size="short" onClick={resetAndClose}>
										{t('sendBookingLinkDialog.done')}
									</Button>
								</div>
							)
					)
					: (
						<>
							<div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
								<ReconnectCalendarBanner />

								{missingCalendarConnection
									? (
										<div
											role="alert"
											className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800"
										>
											<AlertTriangleIcon className="mt-0.5 size-4 shrink-0" />
											<div className="flex-1">
												{t('sendBookingLinkDialog.calendarGuard.message', {
													location: t(
														`sendBookingLinkDialog.location.${effectiveLocationType}`,
													),
												})}
												<>
													<Link
														to="/account/calendar"
														className="font-medium underline"
													>
														{t('sendBookingLinkDialog.calendarGuard.connect')}
													</Link>{' '}
													{t('sendBookingLinkDialog.calendarGuard.chooseAnother')}
												</>
											</div>
										</div>
									)
									: null}
								<div>
									<p className="mb-2 text-sm font-medium text-grey-900">
										{candidateName
											? t('sendBookingLinkDialog.meetingHeading', { name: candidateName })
											: t('sendBookingLinkDialog.meetingHeadingBulk')}
									</p>
									<Listbox
										value={effectiveLocationType}
										onChange={(value) => {
											setLocationType(value)
											setOverrideLocation(value !== campaignDefaultLocation)
										}}
									>
										<div className="relative">
											<ListboxButton className="flex w-full cursor-pointer items-center gap-2 rounded-lg border border-grey-200 px-3 py-2.5 text-left hover:border-grey-300">
												<span className="min-w-0 flex-1">
													{!overrideLocation
														? (
															<span className="block text-xs text-grey-500">
																{t('sendBookingLinkDialog.meetingDefault')}
															</span>
														)
														: null}
													<span className="block truncate text-sm font-medium text-grey-900">
														{t(`sendBookingLinkDialog.location.${effectiveLocationType}`)}
													</span>
												</span>
												<ChevronDownIcon
													className="size-4 shrink-0 text-grey-400"
													aria-hidden="true"
												/>
											</ListboxButton>
											<ListboxOptions
												anchor={{ to: 'bottom start', gap: 4 }}
												className="z-50 max-h-60 w-[var(--button-width)] overflow-auto rounded-lg border border-grey-200 bg-white py-1 shadow-lg focus:outline-none"
											>
												{availableLocationTypes.map((type) => (
													<ListboxOption
														key={type}
														value={type}
														className="flex cursor-pointer items-center justify-between px-3 py-2 text-sm text-grey-700 hover:bg-grey-100 data-[focus]:bg-grey-100"
													>
														<span>{t(`sendBookingLinkDialog.location.${type}`)}</span>
														{effectiveLocationType === type
															? (
																<CheckIcon
																	className="size-4 shrink-0 text-brand-600"
																	aria-hidden="true"
																/>
															)
															: null}
													</ListboxOption>
												))}
											</ListboxOptions>
										</div>
									</Listbox>

									{LOCATION_TYPES_WITH_DETAILS.includes(effectiveLocationType)
										? (
											<input
												type="text"
												value={locationDetails}
												onChange={(event) => setLocationDetails(event.target.value)}
												placeholder={t(
													'sendBookingLinkDialog.locationOverride.detailsPlaceholder',
												)}
												className="mt-2 w-full rounded-lg border border-grey-200 px-3 py-2 text-sm"
											/>
										)
										: null}
								</div>

								<div>
									<p className="mb-2 text-sm font-medium text-grey-900">
										{candidateName
											? t('sendBookingLinkDialog.deliverHeading', { name: candidateName })
											: t('sendBookingLinkDialog.deliverHeadingBulk')}
									</p>
									<div className="space-y-2">
										<div
											className={`rounded-xl border transition-colors ${
												sendMessage
													? 'border-brand-500 bg-brand-50'
													: 'border-grey-200 hover:border-grey-300'
											}`}
										>
											<button
												type="button"
												onClick={() => setSendMessage(true)}
												aria-pressed={sendMessage}
												className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left"
											>
												<span className="min-w-0 flex-1">
													<span className="block text-sm font-semibold text-grey-900">
														{candidateName
															? t('sendBookingLinkDialog.deliverSend', {
																name: candidateName,
															})
															: t('sendBookingLinkDialog.deliverSendBulk')}
													</span>
												</span>
												<span
													className={`grid size-[18px] shrink-0 place-items-center rounded-full border-2 ${
														sendMessage ? 'border-brand-600' : 'border-grey-300'
													}`}
												>
													{sendMessage
														? <span className="size-2 rounded-full bg-brand-600" />
														: null}
												</span>
											</button>

											{sendMessage
												? (
													<div className="space-y-3 px-4 pb-3">
														{whatsAppEnabled
															? (
																<div>
																	<p className="mb-2 text-xs font-medium uppercase tracking-wide text-grey-400">
																		{t('sendBookingLinkDialog.deliverVia')}
																	</p>
																	<div className="flex flex-wrap gap-2">
																		<button
																			type="button"
																			onClick={() => toggleChannel('email')}
																			aria-pressed={emailOn}
																			className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
																				emailOn
																					? 'border-brand-500 bg-brand-50 text-grey-900'
																					: 'border-grey-300 text-grey-500 hover:border-grey-400'
																			}`}
																		>
																			{emailOn
																				? (
																					<CheckIcon
																						className="size-3.5"
																						aria-hidden="true"
																					/>
																				)
																				: null}
																			<MailIcon
																				className="size-3.5"
																				aria-hidden="true"
																			/>
																			{t('sendBookingLinkDialog.channel.email')}
																		</button>
																		<button
																			type="button"
																			onClick={() => toggleChannel('whatsapp')}
																			aria-pressed={whatsappOn}
																			className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
																				whatsappOn
																					? 'border-brand-500 bg-brand-50 text-grey-900'
																					: 'border-grey-300 text-grey-500 hover:border-grey-400'
																			}`}
																		>
																			{whatsappOn
																				? (
																					<CheckIcon
																						className="size-3.5"
																						aria-hidden="true"
																					/>
																				)
																				: null}
																			<MessageCircleIcon
																				className="size-3.5 text-green-600"
																				aria-hidden="true"
																			/>
																			{t('sendBookingLinkDialog.channel.whatsapp')}
																		</button>
																	</div>
																</div>
															)
															: null}

														{emailOn
															? (
																<div className="border-t border-grey-200 pt-3">
																	<div className="mb-1 flex items-center justify-between">
																		<span className="flex items-center gap-1.5 text-xs font-medium text-grey-500">
																			<MailIcon
																				className="size-3.5"
																				aria-hidden="true"
																			/>
																			{t('sendBookingLinkDialog.emailBody.label')}
																		</span>
																		<span className="flex items-center gap-1">
																			<button
																				type="button"
																				onClick={() =>
																					setEmailView(
																						emailView === 'preview'
																							? 'collapsed'
																							: 'preview',
																					)}
																				aria-label={t(
																					'sendBookingLinkDialog.emailPreview.preview',
																				)}
																				aria-pressed={emailView === 'preview'}
																				title={t(
																					'sendBookingLinkDialog.emailPreview.preview',
																				)}
																				className={`flex cursor-pointer items-center rounded p-1 hover:bg-grey-100 ${
																					emailView === 'preview'
																						? 'text-brand-700'
																						: 'text-grey-500'
																				}`}
																			>
																				<EyeIcon
																					className="size-4"
																					aria-hidden="true"
																				/>
																			</button>
																			<button
																				type="button"
																				onClick={() =>
																					setEmailView(
																						emailView === 'edit'
																							? 'collapsed'
																							: 'edit',
																					)}
																				aria-label={t(
																					'sendBookingLinkDialog.emailPreview.edit',
																				)}
																				aria-pressed={emailView === 'edit'}
																				title={t(
																					'sendBookingLinkDialog.emailPreview.edit',
																				)}
																				className={`flex cursor-pointer items-center rounded p-1 hover:bg-grey-100 ${
																					emailView === 'edit'
																						? 'text-brand-700'
																						: 'text-grey-500'
																				}`}
																			>
																				<PencilIcon
																					className="size-4"
																					aria-hidden="true"
																				/>
																			</button>
																		</span>
																	</div>

																	{emailView === 'preview'
																		? (
																			<div className="overflow-hidden rounded-lg border border-grey-200">
																				<EmailBodyPreview html={emailBody} />
																			</div>
																		)
																		: null}

																	{emailView === 'edit'
																		? (
																			<>
																				<div className="overflow-hidden rounded-lg border border-grey-200">
																					<RichLiveField
																						ref={editorRef}
																						value={emailBody}
																						onChange={setEmailBody}
																						outputFormat="html"
																						disableMedia={true}
																						showStaticToolbar={true}
																					/>
																				</div>
																				<div className="mt-2 flex flex-wrap gap-1">
																					{EMAIL_PLACEHOLDERS.map((
																						placeholder,
																					) => (
																						<button
																							key={placeholder}
																							type="button"
																							onClick={() =>
																								insertPlaceholder(
																									placeholder,
																								)}
																							className="rounded-full bg-grey-100 px-2 py-0.5 font-mono text-xs text-grey-600 hover:bg-grey-200"
																						>
																							{placeholder}
																						</button>
																					))}
																				</div>
																			</>
																		)
																		: null}
																</div>
															)
															: null}

														{whatsAppEnabled && whatsappOn
															? (
																<div className="border-t border-grey-200 pt-3">
																	<div className="mb-1 flex items-center justify-between">
																		<span className="flex items-center gap-1.5 text-xs font-medium text-grey-500">
																			<MessageCircleIcon
																				className="size-3.5 text-green-600"
																				aria-hidden="true"
																			/>
																			{t('sendBookingLinkDialog.whatsappBlock.label')}
																			<span className="rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-medium text-brand-700">
																				{t('sendBookingLinkDialog.whatsappBlock.approved')}
																			</span>
																		</span>
																		<button
																			type="button"
																			onClick={() =>
																				setWhatsappView(
																					whatsappView === 'preview'
																						? 'collapsed'
																						: 'preview',
																				)}
																			aria-label={t(
																				'sendBookingLinkDialog.emailPreview.preview',
																			)}
																			aria-pressed={whatsappView === 'preview'}
																			title={t(
																				'sendBookingLinkDialog.emailPreview.preview',
																			)}
																			className={`flex cursor-pointer items-center rounded p-1 hover:bg-grey-100 ${
																				whatsappView === 'preview'
																					? 'text-brand-700'
																					: 'text-grey-500'
																			}`}
																		>
																			<EyeIcon
																				className="size-4"
																				aria-hidden="true"
																			/>
																		</button>
																	</div>

																	{
																		/* Representative preview only — the real approved-template body is not fetched
																		yet (no synced template locally, template shape unconfirmed). Wire this to the
																		resolved WhatsApp template body once that is settled. */
																	}
																	{whatsappView === 'preview'
																		? (
																			<div>
																				<div className="rounded-lg bg-[#d9d2c8] p-3">
																					<div className="max-w-[90%] rounded-lg rounded-tl-sm border border-black/5 bg-white px-2.5 py-2 shadow-sm">
																						<p className="text-[13px] leading-snug text-grey-900">
																							{t('sendBookingLinkDialog.whatsappBlock.sample')}
																						</p>
																						<p className="mt-0.5 text-right text-[10px] text-grey-400">
																							09:41
																						</p>
																						<div className="-mx-2.5 mt-1.5 flex items-center justify-center gap-1.5 border-t border-grey-200 pt-2 text-sm font-medium text-blue-500">
																							{t('sendBookingLinkDialog.whatsappBlock.cta')}
																						</div>
																					</div>
																				</div>
																				<p className="mt-2 text-xs text-grey-400">
																					{t('sendBookingLinkDialog.whatsappBlock.caption')}
																				</p>
																			</div>
																		)
																		: null}
																</div>
															)
															: null}
													</div>
												)
												: null}
										</div>
										<button
											type="button"
											onClick={() => setSendMessage(false)}
											aria-pressed={!sendMessage}
											className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors ${
												!sendMessage
													? 'border-brand-500 bg-brand-50'
													: 'border-grey-200 hover:border-grey-300'
											}`}
										>
											<span className="min-w-0 flex-1">
												<span className="block text-sm font-semibold text-grey-900">
													{t('sendBookingLinkDialog.deliverShare')}
												</span>
												<span className="block text-xs text-grey-500">
													{t('sendBookingLinkDialog.deliverShareDesc')}
												</span>
											</span>
											<span
												className={`grid size-[18px] shrink-0 place-items-center rounded-full border-2 ${
													!sendMessage ? 'border-brand-600' : 'border-grey-300'
												}`}
											>
												{!sendMessage
													? <span className="size-2 rounded-full bg-brand-600" />
													: null}
											</span>
										</button>
									</div>
								</div>

								{conflictIds && conflictIds.length > 0
									? (
										<div className="space-y-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
											<p>
												{t('sendBookingLinkDialog.activeConflict.warning', {
													count: conflictIds.length,
												})}
											</p>
											<Button
												type="subtle"
												size="short"
												onClick={() => void send(true)}
												isLoading={isSending}
												disabled={missingCalendarConnection}
											>
												{t('sendBookingLinkDialog.activeConflict.sendAnyway')}
											</Button>
										</div>
									)
									: null}

								{errorMessage ? <p className="text-sm text-red-600">{errorMessage}</p> : null}
							</div>

							<div className="border-t border-grey-100 px-5 py-4">
								<Button
									type="committal"
									size="short"
									fullWidth={true}
									onClick={() => void send(false)}
									isLoading={isSending}
									disabled={missingCalendarConnection}
								>
									{isBulk
										? t('sendBookingLinkDialog.submitBulk')
										: t('sendBookingLinkDialog.submit')}
								</Button>
							</div>
						</>
					)}
			</div>
		</div>
	)
}
